import { LazyMotion, m } from 'framer-motion'
import { useState } from 'react'
import styled from 'styled-components'
import Tooltip from '@/components/tooltip'
import useIsMobile from '@/hooks/use-is-mobile'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints, colors } from '@/styles'
import TooltipIcon from './tooltip.svg?react'
import Kitty from './kitty.svg?react'

const loadFeatures = () => import('@/components/animations/features').then(res => res.default)

const Wrapper = styled.div``

const AssistantWrapper = styled.div`
  position: fixed;
  bottom: -50px;
  right: 70px;
  z-index: 1;

  @media screen and (min-width: ${breakpoints.desktop}) {
    position: absolute;
    bottom: 10px;
    right: -50px;
    z-index: unset;
    transform: rotate(90deg) scaleX(-1);
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    bottom: -60px;
    right: 35px;
  }
`

const StyledKitty = styled(Kitty)`
  width: 100px;
  height: auto;

  @keyframes wiggleUp {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-1px);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes leftWhiskerWiggle {
    0% {
      transform: rotate(0);
    }
    30% {
      transform: rotate(-3deg);
    }
    80% {
      transform: rotate(2deg);
    }
    100% {
      transform: rotate(0);
    }
  }

  @keyframes rightWhiskerWiggle {
    0% {
      transform: rotate(0);
    }
    30% {
      transform: rotate(3deg);
    }
    80% {
      transform: rotate(-2deg);
    }
    100% {
      transform: rotate(0);
    }
  }

  @keyframes blink {
    0% {
      transform: scaleY(1) skew(0, 0);
    }
    20% {
      transform: scaleY(0.5) skew(30deg, 30deg);
    }
    30%,
    80% {
      transform: scaleY(1) skew(0, 0);
    }
    90% {
      transform: scaleY(0.5) skew(30deg, 30deg);
    }
    100% {
      transform: scaleY(1) skew(0, 0);
    }
  }

  ${props =>
    props.wiggling &&
    `
    animation: wiggleUp 500ms;

    #leftwhiskers {
      animation: leftWhiskerWiggle 700ms ease-in-out;
      transform-origin: 33% 24%;
    }

    #rightwhiskers {
      animation: rightWhiskerWiggle 700ms ease-in-out;
      transform-origin: 46% 30%;
    }

    #lefteye {
      animation: blink 1000ms;
      transform-origin: 38% 18%;
    }

    #righteye {
      animation: blink 1000ms;
      transform-origin: 50% 24%;
    }
  `}
`

const TooltipWrapper = styled.div`
  position: fixed;
  bottom: 40px;
  right: 70px;
  z-index: 1;

  @media screen and (min-width: ${breakpoints.desktop}) {
    position: absolute;
    bottom: 90px;
    right: -65px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    bottom: 30px;
    right: 35px;
  }
`

const StyledTooltipIcon = styled(TooltipIcon)`
  width: 20px;
  height: 20px;
  cursor: pointer;
`

const variants = {
  fromSlide: {
    y: 200
  },
  toSlide: {
    y: 0
  },
  fromFade: {
    opacity: 0
  },
  toFade: {
    opacity: 1
  }
}

const config = { type: 'spring', damping: 18, mass: 1.2, stiffness: 100 }

const CatTooltip = ({ message }) => {
  const [isWiggling, setIsWiggling] = useState(false)
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const displayDesktop = useMediaQuery(`(min-width: ${breakpoints.desktop})`)
  const animation = displayDesktop ? 'Fade' : 'Slide'
  const userAgentMobile = useIsMobile()

  const wiggle = () => {
    if (isWiggling) return
    setIsWiggling(true)
    setTimeout(() => {
      setIsWiggling(false)
    }, 1000)
  }

  return (
    <LazyMotion features={loadFeatures}>
      <Wrapper>
        <AssistantWrapper>
          <m.div
            key={`cat-${animation}`}
            initial="fromSlide"
            animate="toSlide"
            variants={variants}
            transition={config}
          >
            <StyledKitty wiggling={isWiggling.toString()} />
          </m.div>
        </AssistantWrapper>
        <TooltipWrapper>
          <m.div
            key={`tooltip-${animation}`}
            initial={`from${animation}`}
            animate={`to${animation}`}
            variants={variants}
            transition={config}
          >
            <Tooltip
              text={message}
              position="top"
              background={colors.link}
              contentWidth={190}
              offsetLeft={displayDesktop ? 0 : -70}
              showCloseButton={displayMobile || userAgentMobile}
            >
              <StyledTooltipIcon onMouseEnter={wiggle} />
            </Tooltip>
          </m.div>
        </TooltipWrapper>
      </Wrapper>
    </LazyMotion>
  )
}

export default CatTooltip
